import { useState } from 'react';
import { Heart, Package, Users, EyeOff, MessageCircle, X, CheckCircle, Phone } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { apiUrl } from '../../utils/api';
import { ProductWithAvailability, Campaign } from '../../hooks/useCampaignDetail';
import { PhoneCodeSelect } from '../PhoneCodeSelect';

interface CommitmentItem {
  productId: string;
  quantity: number;
}

interface Props {
  campaign: Campaign;
  products: ProductWithAvailability[];
  initialItems: CommitmentItem[];
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

export function DonationFormModal({ campaign, products, initialItems, onClose, onSuccess, onError }: Props) {
  const [donationItems, setDonationItems] = useState<CommitmentItem[]>(initialItems);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [donorInfo, setDonorInfo] = useState({ name: '', email: '', phoneCode: '+57', phone: '', isAnonymous: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ whatsappUrl: string | null } | null>(null);

  const updateQuantity = (productId: string, quantity: number, max: number) => {
    const clamped = Math.max(1, Math.min(quantity, max));
    setDonationItems(donationItems.map(i =>
      i.productId === productId ? { ...i, quantity: clamped } : i
    ));
    // Sync the input display value
    setInputValues(prev => ({ ...prev, [productId]: String(clamped) }));
  };

  const handleInputChange = (productId: string, rawValue: string) => {
    // Allow free typing — just store the raw string, no validation yet
    setInputValues(prev => ({ ...prev, [productId]: rawValue }));
  };

  const handleInputBlur = (productId: string, max: number) => {
    const raw = inputValues[productId];
    const parsed = parseInt(raw) || 0;
    const clamped = parsed < 1 ? 1 : parsed > max ? max : parsed;
    setDonationItems(donationItems.map(i =>
      i.productId === productId ? { ...i, quantity: clamped } : i
    ));
    setInputValues(prev => ({ ...prev, [productId]: String(clamped) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(apiUrl('/api/commitments'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: campaign.id,
          donorName: donorInfo.isAnonymous ? 'Donante Anónimo' : donorInfo.name,
          donorEmail: donorInfo.email,
          donorPhone: `${donorInfo.phoneCode} ${donorInfo.phone}`,
          items: donationItems,
          isAnonymous: donorInfo.isAnonymous,
        }),
      });

      const data = await response.json();

      if (data.success) {
        const donationSummary = donationItems.map(item => {
          const product = products.find(p => p.id === item.productId);
          return `${item.quantity} ${product?.unit || 'unidades'} de ${product?.name || 'producto'}`;
        }).join(', ');

        const campaignPhone = campaign.contactInfo?.phone;
        let whatsappUrl: string | null = null;
        if (campaignPhone) {
          const cleanPhone = campaignPhone.replace(/\D/g, '');
          const phoneWithCode = cleanPhone.startsWith('57') ? cleanPhone : `57${cleanPhone}`;
          const msg = `Hola, quiero aportar ${donationSummary} para la campaña "${campaign.title}". Mi nombre es ${donorInfo.name}.`;
          whatsappUrl = `https://wa.me/${phoneWithCode}?text=${encodeURIComponent(msg)}`;
        }

        setSuccessInfo({ whatsappUrl });
      } else {
        onError(data.error?.message || 'Error al procesar el aporte');
      }
    } catch {
      onError('Error de conexión. Por favor, intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">

        {/* Success screen */}
        {successInfo ? (
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">¡Gracias por tu aporte!</h3>
            <p className="text-sm text-gray-600 mb-6">
              Tu compromiso ha sido registrado exitosamente. El organizador podrá ver tu aporte.
            </p>

            {successInfo.whatsappUrl && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-green-800 font-medium mb-3">
                  Coordina la entrega por WhatsApp
                </p>
                <a
                  href={successInfo.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center space-x-2 w-full px-4 py-3 bg-[#25D366] text-white rounded-lg font-medium hover:bg-[#1fb855] transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5" />
                  <span>Contactar por WhatsApp</span>
                </a>
              </div>
            )}

            {!successInfo.whatsappUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-center space-x-2 text-blue-800">
                  <Phone className="w-4 h-4" />
                  <p className="text-sm font-medium">El organizador se pondrá en contacto contigo</p>
                </div>
              </div>
            )}

            <button
              onClick={onSuccess}
              className="w-full px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
            >
              Cerrar
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
                <Heart className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmar Aporte</h3>
                <p className="text-sm text-gray-500">Tu ayuda hace la diferencia</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Items */}
          <div className="mb-5">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Package className="w-4 h-4 mr-2" />
              Productos seleccionados
            </h4>
            <div className="space-y-2">
              {donationItems.map((item) => {
                const product = products.find(p => p.id === item.productId);
                if (!product) return null;
                return (
                  <div key={item.productId} className="bg-gray-50 border border-gray-200 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-500">Faltan: {product.availableQuantity} {product.unit}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.productId, product.availableQuantity, product.availableQuantity)}
                        className="text-[10px] text-indigo-600 font-medium bg-indigo-50 px-2 py-1 rounded hover:bg-indigo-100 transition-colors whitespace-nowrap"
                      >
                        Aportar todo
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button type="button" onClick={() => updateQuantity(item.productId, item.quantity - 1, product.availableQuantity)}
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 text-sm flex-shrink-0">-</button>
                      <input
                        type="number"
                        min={1}
                        max={product.availableQuantity}
                        value={inputValues[item.productId] ?? item.quantity}
                        onChange={(e) => handleInputChange(item.productId, e.target.value)}
                        onBlur={() => handleInputBlur(item.productId, product.availableQuantity)}
                        className="w-16 text-center font-semibold text-sm border border-gray-300 rounded-lg py-1 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button type="button" onClick={() => updateQuantity(item.productId, item.quantity + 1, product.availableQuantity)}
                        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 text-sm flex-shrink-0">+</button>
                      <span className="text-xs text-gray-500 ml-1">{product.unit}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Donor info */}
          <div className="mb-5">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Tu información
            </h4>
            <div className="space-y-3">
              <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                <button
                  type="button"
                  role="switch"
                  aria-checked={donorInfo.isAnonymous}
                  onClick={() => setDonorInfo({ ...donorInfo, isAnonymous: !donorInfo.isAnonymous })}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors mr-2 ${donorInfo.isAnonymous ? 'bg-indigo-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${donorInfo.isAnonymous ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                </button>
                <EyeOff className="w-3.5 h-3.5 mr-1" />
                Aporte anónimo
              </label>
              {donorInfo.isAnonymous && (
                <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                  <EyeOff className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>Tu nombre no se mostrará públicamente. Aun así necesitamos tus datos para coordinar la entrega — no serán compartidos.</span>
                </p>
              )}
              <input type="text" required value={donorInfo.name}
                onChange={(e) => setDonorInfo({ ...donorInfo, name: e.target.value })}
                className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Tu nombre completo *" />
              <input type="email" required value={donorInfo.email}
                onChange={(e) => setDonorInfo({ ...donorInfo, email: e.target.value })}
                className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="tu@email.com *" />
              <div className="flex gap-2">
                <div className="w-28 flex-shrink-0">
                  <PhoneCodeSelect
                    value={donorInfo.phoneCode}
                    onChange={(code) => setDonorInfo({ ...donorInfo, phoneCode: code })}
                  />
                </div>
                <input type="tel" required value={donorInfo.phone}
                  onChange={(e) => setDonorInfo({ ...donorInfo, phone: e.target.value })}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="300 123 4567 *" />
              </div>
            </div>
          </div>

          {campaign.contactInfo?.phone && (
            <div className="mb-5 bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center space-x-2 text-sm text-green-800">
                <MessageCircle className="w-4 h-4" />
                <span>Después podrás coordinar la entrega por WhatsApp</span>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
              Cancelar
            </button>
            <button type="submit" disabled={isSubmitting || donationItems.length === 0}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center space-x-2">
              {isSubmitting ? <span>Procesando...</span> : <><Heart className="w-4 h-4" /><span>Confirmar</span></>}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
